import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import ThreeGlobe from 'three-globe';

/**
 * Hero3DGlobe — premium cinematic Earth.
 *  - Real NASA-style day + bump + specular textures (no procedural polygons)
 *  - Cool sky-blue atmosphere bloom
 *  - 3-point cinematic lighting (warm sun key + bright blue rim + soft fill)
 *  - 18 worldwide connection nodes + 12 curated cross-continental arcs
 *  - Cool stardust drift, drag-to-spin, mouse parallax, auto-rotation
 */

const EARTH_DAY  = 'https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg';
const EARTH_BUMP = 'https://unpkg.com/three-globe/example/img/earth-topology.png';
const EARTH_SPEC = 'https://unpkg.com/three-globe/example/img/earth-water.png';

// Major hub cities for the curated arc routes (indices used by buildArcs)
const HUB_CITIES = [
    // North America
    { name: 'New York', lat: 40.7128, lng: -74.006 },
    { name: 'Toronto', lat: 43.6532, lng: -79.3832 },
    { name: 'San Francisco', lat: 37.7749, lng: -122.4194 },
    // South America
    { name: 'São Paulo', lat: -23.5505, lng: -46.6333 },
    { name: 'Buenos Aires', lat: -34.6037, lng: -58.3816 },
    // Europe
    { name: 'London', lat: 51.5074, lng: -0.1278 },
    { name: 'Paris', lat: 48.8566, lng: 2.3522 },
    { name: 'Berlin', lat: 52.52, lng: 13.405 },
    { name: 'Moscow', lat: 55.7558, lng: 37.6173 },
    // Africa
    { name: 'Lagos', lat: 6.5244, lng: 3.3792 },
    { name: 'Cape Town', lat: -33.9249, lng: 18.4241 },
    { name: 'Nairobi', lat: -1.2921, lng: 36.8219 },
    // Middle East / Asia
    { name: 'Dubai', lat: 25.2048, lng: 55.2708 },
    { name: 'Mumbai', lat: 19.076, lng: 72.8777 },
    { name: 'Singapore', lat: 1.3521, lng: 103.8198 },
    { name: 'Tokyo', lat: 35.6762, lng: 139.6503 },
    { name: 'Shanghai', lat: 31.2304, lng: 121.4737 },
    // Oceania
    { name: 'Sydney', lat: -33.8688, lng: 151.2093 },
];

// Additional scatter of city-glow dots across every continent — gives the
// reference's "alive global AI network" feel.
const SCATTER_CITIES = (() => {
    const base = [
        // North America
        {lat:34.0522,lng:-118.2437},{lat:41.8781,lng:-87.6298},{lat:25.7617,lng:-80.1918},
        {lat:29.7604,lng:-95.3698},{lat:45.5017,lng:-73.5673},{lat:47.6062,lng:-122.3321},
        {lat:39.7392,lng:-104.9903},{lat:19.4326,lng:-99.1332},{lat:32.7157,lng:-117.1611},
        {lat:38.9072,lng:-77.0369},{lat:33.749,lng:-84.388},{lat:42.3601,lng:-71.0589},
        {lat:36.1627,lng:-86.7816},{lat:35.2271,lng:-80.8431},{lat:30.2672,lng:-97.7431},
        {lat:44.9778,lng:-93.265},{lat:49.2827,lng:-123.1207},{lat:51.0447,lng:-114.0719},
        {lat:39.0997,lng:-94.5786},{lat:35.1495,lng:-90.049},{lat:36.7783,lng:-119.4179},
        {lat:33.4484,lng:-112.074},{lat:40.4406,lng:-79.9959},{lat:39.9526,lng:-75.1652},
        // South America
        {lat:-12.0464,lng:-77.0428},{lat:-22.9068,lng:-43.1729},{lat:4.711,lng:-74.0721},
        {lat:-33.4489,lng:-70.6693},{lat:-0.1807,lng:-78.4678},{lat:10.4806,lng:-66.9036},
        {lat:-16.5,lng:-68.15},{lat:-25.2637,lng:-57.5759},{lat:-2.1894,lng:-79.8891},
        {lat:-15.7942,lng:-47.8825},{lat:-30.0346,lng:-51.2177},{lat:-27.4949,lng:-58.829},
        {lat:6.2442,lng:-75.5812},{lat:-3.4653,lng:-62.2159},{lat:-8.0476,lng:-34.877},
        // Europe
        {lat:40.4168,lng:-3.7038},{lat:41.9028,lng:12.4964},{lat:52.3676,lng:4.9041},
        {lat:50.0755,lng:14.4378},{lat:59.3293,lng:18.0686},{lat:48.2082,lng:16.3738},
        {lat:55.6761,lng:12.5683},{lat:60.1699,lng:24.9384},{lat:53.3498,lng:-6.2603},
        {lat:38.7223,lng:-9.1393},{lat:47.4979,lng:19.0402},{lat:50.8503,lng:4.3517},
        {lat:53.4808,lng:-2.2426},{lat:55.9533,lng:-3.1883},{lat:43.6047,lng:1.4442},
        {lat:50.937,lng:6.9603},{lat:48.1351,lng:11.582},{lat:50.1109,lng:8.6821},
        {lat:51.5142,lng:7.4653},{lat:44.4268,lng:26.1025},{lat:42.6977,lng:23.3219},
        {lat:45.4642,lng:9.19},{lat:43.7696,lng:11.2558},{lat:37.9755,lng:23.7348},
        {lat:46.0569,lng:14.5058},{lat:45.815,lng:15.9819},{lat:46.2044,lng:6.1432},
        {lat:51.05,lng:13.74},{lat:52.0907,lng:5.1214},{lat:54.687,lng:25.2797},
        // Africa
        {lat:30.0444,lng:31.2357},{lat:33.5731,lng:-7.5898},{lat:-26.2041,lng:28.0473},
        {lat:9.0579,lng:7.4951},{lat:14.6928,lng:-17.4467},{lat:5.6037,lng:-0.187},
        {lat:36.7538,lng:3.0588},{lat:-4.4419,lng:15.2663},{lat:15.5007,lng:32.5599},
        {lat:9.0192,lng:38.7525},{lat:0.3476,lng:32.5825},{lat:11.5021,lng:43.146},
        {lat:24.4539,lng:54.3773},{lat:-15.387,lng:28.3228},{lat:-29.3151,lng:27.4869},
        {lat:7.7942,lng:-3.2986},{lat:12.6392,lng:-8.0029},{lat:13.5128,lng:2.1128},
        {lat:36.8065,lng:10.1815},{lat:31.6295,lng:-7.9811},
        // Middle East / Asia
        {lat:35.6892,lng:51.389},{lat:31.7683,lng:35.2137},{lat:39.9334,lng:32.8597},
        {lat:21.4225,lng:39.8262},{lat:24.7136,lng:46.6753},{lat:33.3152,lng:44.3661},
        {lat:30.5728,lng:104.0668},{lat:22.3193,lng:114.1694},{lat:13.7563,lng:100.5018},
        {lat:14.5995,lng:120.9842},{lat:-6.2088,lng:106.8456},{lat:3.139,lng:101.6869},
        {lat:23.8103,lng:90.4125},{lat:27.7172,lng:85.324},{lat:33.6844,lng:73.0479},
        {lat:34.0522,lng:69.1813},{lat:43.2389,lng:76.8897},{lat:41.0082,lng:28.9784},
        {lat:35.6762,lng:51.4231},{lat:37.5665,lng:126.978},{lat:39.9042,lng:116.4074},
        {lat:25.0330,lng:121.5654},{lat:28.6139,lng:77.2090},{lat:12.9716,lng:77.5946},
        {lat:11.0168,lng:76.9558},{lat:17.385,lng:78.4867},{lat:23.0225,lng:72.5714},
        {lat:18.5204,lng:73.8567},{lat:8.5241,lng:76.9366},{lat:34.7465,lng:113.6253},
        {lat:30.5728,lng:114.3055},{lat:39.0851,lng:117.1994},{lat:36.0611,lng:103.8343},
        {lat:43.8256,lng:87.6168},{lat:34.6937,lng:135.5023},{lat:34.6901,lng:135.1956},
        {lat:35.18,lng:136.9066},{lat:43.0642,lng:141.3469},{lat:33.5904,lng:130.4017},
        {lat:1.3521,lng:103.8198},{lat:21.0285,lng:105.8542},{lat:16.0544,lng:108.2022},
        {lat:21.5928,lng:39.1031},{lat:32.0853,lng:34.7818},{lat:33.8869,lng:35.5131},
        // Oceania
        {lat:-37.8136,lng:144.9631},{lat:-31.9505,lng:115.8605},{lat:-27.4698,lng:153.0251},
        {lat:-41.2865,lng:174.7762},{lat:-34.9285,lng:138.6007},{lat:-43.5321,lng:172.6362},
        {lat:-36.8485,lng:174.7633},{lat:-12.4634,lng:130.8456},
    ];
    return base;
})();

const ALL_CITIES = [...HUB_CITIES, ...SCATTER_CITIES];

const NODE_COLOR   = '#FF6A2A';   // hot red-orange glow (matches reference)
const ARC_HOT      = 'rgba(255,110,40,0.95)';   // hot orange
const ARC_TAIL     = 'rgba(255,60,40,0.08)';    // dim red trail

const buildArcs = (cities) => {
    // Curated cross-continental routes — premium, dense but never random.
    const routes = [
        [0, 5],   // New York   → London
        [2, 15],  // San Francisco → Tokyo
        [5, 11],  // London     → Nairobi
        [6, 12],  // Paris      → Dubai
        [7, 16],  // Berlin     → Shanghai
        [3, 9],   // São Paulo  → Lagos
        [12, 13], // Dubai      → Mumbai
        [13, 14], // Mumbai     → Singapore
        [14, 17], // Singapore  → Sydney
        [11, 10], // Nairobi    → Cape Town
        [0, 3],   // New York   → São Paulo
        [8, 12],  // Moscow     → Dubai
        [2, 14],  // SF         → Singapore
        [15, 17], // Tokyo      → Sydney
        [1, 6],   // Toronto    → Paris
        [0, 6],   // New York   → Paris
        [5, 12],  // London     → Dubai
        [6, 13],  // Paris      → Mumbai
        [7, 8],   // Berlin     → Moscow
        [16, 15], // Shanghai   → Tokyo
        [9, 12],  // Lagos      → Dubai
        [3, 5],   // São Paulo  → London
        [4, 10],  // Buenos Aires → Cape Town
        [4, 0],   // Buenos Aires → New York
        [1, 5],   // Toronto    → London
        [2, 17],  // SF         → Sydney
        [8, 16],  // Moscow     → Shanghai
        [11, 12], // Nairobi    → Dubai
        [13, 16], // Mumbai     → Shanghai
        [14, 16], // Singapore  → Shanghai
        [10, 17], // Cape Town  → Sydney
        [0, 2],   // NY         → SF
    ];
    return routes.map(([i, j]) => {
        const a = cities[i];
        const b = cities[j];
        return {
            startLat: a.lat,
            startLng: a.lng,
            endLat: b.lat,
            endLng: b.lng,
        };
    });
};

const Hero3DGlobe = () => {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;
        const width = mount.clientWidth;
        const height = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 2000);
        camera.position.set(0, 0, 300);

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(width, height);
        renderer.setClearColor(0xffffff, 0);
        mount.appendChild(renderer.domElement);

        const globe = new ThreeGlobe({ waitForGlobeReady: true, animateIn: true })
            // Real NASA Blue Marble Earth — premium cinematic realism
            .globeImageUrl(EARTH_DAY)
            .bumpImageUrl(EARTH_BUMP)
            .showAtmosphere(true)
            .atmosphereColor('#7DD3FC')
            .atmosphereAltitude(0.28)
            // Hot red-orange glowing city nodes (worldwide network)
            .pointsData(ALL_CITIES)
            .pointLat('lat')
            .pointLng('lng')
            .pointColor(() => NODE_COLOR)
            .pointAltitude(0.01)
            .pointRadius(0.34)
            .pointResolution(16)
            // Soft pulsing rings only on hub cities (avoid clutter)
            .ringsData(HUB_CITIES)
            .ringLat('lat')
            .ringLng('lng')
            .ringColor(() => (t) => `rgba(255, 140, 70, ${0.7 * (1 - t)})`)
            .ringMaxRadius(3.4)
            .ringPropagationSpeed(1.6)
            .ringRepeatPeriod(2400)
            // Cross-continental connection arcs — curated, smooth, glowing
            .arcsData(buildArcs(HUB_CITIES))
            .arcStartLat('startLat')
            .arcStartLng('startLng')
            .arcEndLat('endLat')
            .arcEndLng('endLng')
            .arcColor(() => [ARC_HOT, ARC_TAIL])
            .arcDashLength(0.72)
            .arcDashGap(0.35)
            .arcDashAnimateTime(3200)
            .arcStroke(0.55)
            .arcAltitudeAutoScale(0.55);

        // Tune the auto-created globe material for cinematic glossy oceans
        {
            const mat = globe.globeMaterial();
            if (mat) {
                const loader = new THREE.TextureLoader();
                mat.specularMap = loader.load(EARTH_SPEC);
                mat.specular = new THREE.Color(0x88C5F0);
                mat.shininess = 32;
                mat.bumpScale = 10;
                mat.needsUpdate = true;
            }
        }

        // Outer atmosphere glow shell — adds a wider, softer halo on top of the
        // built-in atmosphere so the planet has a strong cinematic edge bloom.
        const glowGeo = new THREE.SphereGeometry(globe.getGlobeRadius() * 1.18, 64, 48);
        const glowMat = new THREE.ShaderMaterial({
            uniforms: { c: { value: 0.35 }, p: { value: 5.0 }, glowColor: { value: new THREE.Color(0x7DD3FC) } },
            vertexShader: `
                varying vec3 vNormal;
                void main() {
                    vNormal = normalize(normalMatrix * normal);
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }`,
            fragmentShader: `
                uniform vec3 glowColor;
                uniform float c;
                uniform float p;
                varying vec3 vNormal;
                void main() {
                    float intensity = pow(c - dot(vNormal, vec3(0.0, 0.0, 1.0)), p);
                    gl_FragColor = vec4(glowColor, 1.0) * intensity;
                }`,
            side: THREE.BackSide,
            blending: THREE.AdditiveBlending,
            transparent: true,
            depthWrite: false,
        });
        const atmGlow = new THREE.Mesh(glowGeo, glowMat);
        scene.add(atmGlow);

        // Procedural glow sprite texture (canvas radial gradient) — used for both
        // city bloom dots and engine bloom on the ships.
        const makeGlowTex = (rgb) => {
            const c = document.createElement('canvas');
            c.width = 128;
            c.height = 128;
            const ctx = c.getContext('2d');
            const grad = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
            grad.addColorStop(0,    `rgba(${rgb},1)`);
            grad.addColorStop(0.2,  `rgba(${rgb},0.85)`);
            grad.addColorStop(0.45, `rgba(${rgb},0.35)`);
            grad.addColorStop(1,    `rgba(${rgb},0)`);
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, 128, 128);
            const tex = new THREE.CanvasTexture(c);
            tex.colorSpace = THREE.SRGBColorSpace;
            return tex;
        };
        const cityGlowTex = makeGlowTex('255,140,80');

        // City bloom sprites — bright additive halos sit on top of every city node
        const cityGlowGroup = new THREE.Group();
        const R = globe.getGlobeRadius();
        ALL_CITIES.forEach((city) => {
            const phi   = (90 - city.lat) * Math.PI / 180;
            const theta = (city.lng + 180) * Math.PI / 180;
            const r = R * 1.012;
            const x = -r * Math.sin(phi) * Math.cos(theta);
            const z =  r * Math.sin(phi) * Math.sin(theta);
            const y =  r * Math.cos(phi);
            const spr = new THREE.Sprite(
                new THREE.SpriteMaterial({
                    map: cityGlowTex,
                    color: 0xFF8C50,
                    transparent: true,
                    opacity: 0.95,
                    blending: THREE.AdditiveBlending,
                    depthWrite: false,
                })
            );
            spr.position.set(x, y, z);
            spr.scale.setScalar(3.6);
            cityGlowGroup.add(spr);
        });
        scene.add(cityGlowGroup);

        scene.add(globe);

        // ===== Deep-space starfield — 1500 tiny white stars far behind everything =====
        const starCount = 1500;
        const starGeo = new THREE.BufferGeometry();
        const starPos = new Float32Array(starCount * 3);
        for (let i = 0; i < starCount; i++) {
            const r = 600 + Math.random() * 600;
            const u = Math.random();
            const v = Math.random();
            const theta = 2 * Math.PI * u;
            const phi = Math.acos(2 * v - 1);
            starPos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
            starPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
            starPos[i * 3 + 2] = r * Math.cos(phi);
        }
        starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
        const starMat = new THREE.PointsMaterial({
            color: 0xFFFFFF,
            size: 1.4,
            transparent: true,
            opacity: 0.9,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
        });
        const stars = new THREE.Points(starGeo, starMat);
        scene.add(stars);

        // ===== 3D spacecraft — 4 ships orbiting the globe at varied radii / speeds =====
        const ships = [];
        const createShip = (color) => {
            const grp = new THREE.Group();
            // Main hull — elongated dark metallic body
            const hull = new THREE.Mesh(
                new THREE.ConeGeometry(3.2, 12, 8),
                new THREE.MeshStandardMaterial({
                    color: 0x1A1A2E,
                    metalness: 0.9,
                    roughness: 0.3,
                    emissive: 0x0A1428,
                    emissiveIntensity: 0.5,
                })
            );
            hull.rotation.z = Math.PI / 2;
            grp.add(hull);
            // Side wings — thin metallic slabs for silhouette
            const wingMat = new THREE.MeshStandardMaterial({
                color: 0x0A1424, metalness: 0.85, roughness: 0.4,
            });
            const wingL = new THREE.Mesh(new THREE.BoxGeometry(6, 0.4, 2.4), wingMat);
            wingL.position.set(-1, 0,  2.6);
            grp.add(wingL);
            const wingR = new THREE.Mesh(new THREE.BoxGeometry(6, 0.4, 2.4), wingMat);
            wingR.position.set(-1, 0, -2.6);
            grp.add(wingR);
            // Engine core glow
            const engineMat = new THREE.MeshBasicMaterial({
                color, transparent: true, opacity: 1.0,
                blending: THREE.AdditiveBlending, depthWrite: false,
            });
            const engine = new THREE.Mesh(new THREE.SphereGeometry(2.2, 16, 12), engineMat);
            engine.position.x = -6.2;
            grp.add(engine);
            // Engine bloom sprite — large additive halo for cinematic glow
            const engineGlowTex = makeGlowTex(
                color === 0xFF6A2A || color === 0xFF8A4D ? '255,120,70' : '120,200,255'
            );
            const engineBloom = new THREE.Sprite(new THREE.SpriteMaterial({
                map: engineGlowTex, color, transparent: true,
                opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false,
            }));
            engineBloom.position.set(-6.2, 0, 0);
            engineBloom.scale.setScalar(14);
            grp.add(engineBloom);
            // Engine trail
            const trail = new THREE.Mesh(
                new THREE.ConeGeometry(1.4, 12, 12, 1, true),
                new THREE.MeshBasicMaterial({
                    color, transparent: true, opacity: 0.55,
                    blending: THREE.AdditiveBlending, depthWrite: false,
                    side: THREE.DoubleSide,
                })
            );
            trail.rotation.z = -Math.PI / 2;
            trail.position.x = -13;
            grp.add(trail);
            return grp;
        };

        const SHIP_DATA = [
            { radius: 158, color: 0xFF6A2A, speed: 0.18, tilt:  0.30, phase: 0.0, scale: 1.4 },
            { radius: 175, color: 0x60A5FA, speed: 0.12, tilt: -0.55, phase: 1.8, scale: 1.15 },
            { radius: 192, color: 0xFF8A4D, speed: 0.10, tilt:  0.95, phase: 3.6, scale: 1.3 },
            { radius: 208, color: 0x7DD3FC, speed: 0.08, tilt: -0.20, phase: 5.0, scale: 1.05 },
        ];
        SHIP_DATA.forEach((d) => {
            const s = createShip(d.color);
            s.scale.setScalar(d.scale);
            scene.add(s);
            ships.push({ mesh: s, ...d });
        });

        // ===== Soft ambient stardust close to the globe — cool blue tones =====
        const ambCount = 220;
        const ambGeo = new THREE.BufferGeometry();
        const ambPos = new Float32Array(ambCount * 3);
        const ambCol = new Float32Array(ambCount * 3);
        const skyCol = new THREE.Color(0x7DD3FC);
        const iceCol = new THREE.Color(0xE0F2FE);
        const azureCol = new THREE.Color(0x60A5FA);
        for (let i = 0; i < ambCount; i++) {
            const r = 200 + Math.random() * 160;
            const u = Math.random();
            const v = Math.random();
            const theta = 2 * Math.PI * u;
            const phi = Math.acos(2 * v - 1);
            ambPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
            ambPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
            ambPos[i * 3 + 2] = r * Math.cos(phi);
            const palette = i % 3 === 0 ? skyCol : i % 3 === 1 ? iceCol : azureCol;
            ambCol[i * 3] = palette.r;
            ambCol[i * 3 + 1] = palette.g;
            ambCol[i * 3 + 2] = palette.b;
        }
        ambGeo.setAttribute('position', new THREE.BufferAttribute(ambPos, 3));
        ambGeo.setAttribute('color', new THREE.BufferAttribute(ambCol, 3));
        const ambMat = new THREE.PointsMaterial({
            size: 1.1,
            vertexColors: true,
            transparent: true,
            opacity: 0.55,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
        });
        const ambient = new THREE.Points(ambGeo, ambMat);
        scene.add(ambient);

        scene.add(new THREE.AmbientLight(0xE0F2FF, 0.75));
        // Warm sun key-light — strong cinematic highlight on the day side
        const sunLight = new THREE.DirectionalLight(0xFFF0D8, 1.15);
        sunLight.position.set(240, 200, 280);
        scene.add(sunLight);
        // Bright blue rim/back-light — wraps around the silhouette for the bloom feel
        const rimLight = new THREE.DirectionalLight(0x7DD3FC, 0.85);
        rimLight.position.set(-280, -60, 140);
        scene.add(rimLight);
        // Subtle bottom fill — soft glow from the platform ripple below
        const fillLight = new THREE.DirectionalLight(0xBAE0FF, 0.35);
        fillLight.position.set(0, -260, 60);
        scene.add(fillLight);

        // --- Interaction: mouse parallax + drag-to-spin ---
        const mouse = { tx: 0, ty: 0, x: 0, y: 0 };
        const drag = {
            active: false,
            lastX: 0,
            lastY: 0,
            velX: 0,
            velY: 0,
        };
        // Manual rotation tracker
        const rot = { x: 0, y: 0, autoY: 0 };

        const onMouseMove = (e) => {
            const rect = mount.getBoundingClientRect();
            mouse.tx = (e.clientX - rect.left) / rect.width - 0.5;
            mouse.ty = (e.clientY - rect.top) / rect.height - 0.5;
            if (drag.active) {
                const dx = e.clientX - drag.lastX;
                const dy = e.clientY - drag.lastY;
                drag.velY = dx * 0.005;
                drag.velX = dy * 0.005;
                rot.y += drag.velY;
                rot.x += drag.velX;
                rot.x = Math.max(-1.2, Math.min(1.2, rot.x));
                drag.lastX = e.clientX;
                drag.lastY = e.clientY;
            }
        };
        const onPointerDown = (e) => {
            drag.active = true;
            drag.lastX = e.clientX;
            drag.lastY = e.clientY;
            drag.velX = 0;
            drag.velY = 0;
            mount.style.cursor = 'grabbing';
        };
        const onPointerUp = () => {
            drag.active = false;
            mount.style.cursor = 'grab';
        };

        renderer.domElement.style.cursor = 'grab';
        mount.style.touchAction = 'pan-y';
        window.addEventListener('mousemove', onMouseMove);
        renderer.domElement.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointerup', onPointerUp);

        const onResize = () => {
            const w = mount.clientWidth, h = mount.clientHeight;
            renderer.setSize(w, h);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', onResize);

        const clock = new THREE.Clock();
        const AUTO_SPEED = (Math.PI * 2) / 30; // 30s per revolution
        let raf = 0;
        const tick = () => {
            raf = requestAnimationFrame(tick);
            const dt = clock.getDelta();
            const t = clock.getElapsedTime();

            // Smooth mouse interpolation
            mouse.x += (mouse.tx - mouse.x) * 0.05;
            mouse.y += (mouse.ty - mouse.y) * 0.05;

            // Auto rotation (only when not dragging)
            if (!drag.active) {
                rot.autoY += AUTO_SPEED * dt;
                // Decay manual momentum
                drag.velX *= 0.94;
                drag.velY *= 0.94;
                rot.y += drag.velY;
                rot.x += drag.velX;
                rot.x = Math.max(-1.2, Math.min(1.2, rot.x));
            }

            // Apply combined rotation: auto + manual + parallax tilt
            globe.rotation.y = rot.y + rot.autoY + mouse.x * 0.5;
            globe.rotation.x = rot.x + mouse.y * 0.3;
            // City bloom sprites rotate with the globe so they stay anchored
            cityGlowGroup.rotation.copy(globe.rotation);
            // Atmosphere glow shell follows the globe orientation
            atmGlow.rotation.copy(globe.rotation);

            // Ambient particles slow drift
            ambient.rotation.y = -t * 0.04;
            ambient.rotation.x = Math.sin(t * 0.07) * 0.1;

            // Starfield very slow drift for depth
            stars.rotation.y = -t * 0.008;

            // Spacecraft orbits — each ship circles the globe at its own tilt & speed,
            // and orients its nose along the direction of travel.
            ships.forEach((s) => {
                const angle = s.phase + t * s.speed;
                const cosT = Math.cos(s.tilt);
                const sinT = Math.sin(s.tilt);
                const x =  Math.cos(angle) * s.radius;
                const y0 = Math.sin(angle) * s.radius;
                // Rotate the orbit plane around the X axis by `tilt`
                const yT = y0 * cosT;
                const zT = y0 * sinT;
                s.mesh.position.set(x, yT, zT);
                // Tangent direction = d/d(angle)
                const tx = -Math.sin(angle) * s.radius;
                const tyT = Math.cos(angle) * s.radius * cosT;
                const tzT = Math.cos(angle) * s.radius * sinT;
                s.mesh.lookAt(
                    s.mesh.position.x + tx,
                    s.mesh.position.y + tyT,
                    s.mesh.position.z + tzT
                );
            });

            renderer.render(scene, camera);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMouseMove);
            renderer.domElement.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('resize', onResize);
            if (mount && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            ambGeo.dispose();
            ambMat.dispose();
            starGeo.dispose();
            starMat.dispose();
            glowGeo.dispose();
            glowMat.dispose();
            cityGlowGroup.traverse((obj) => {
                if (obj.material) {
                    if (obj.material.map) obj.material.map.dispose();
                    obj.material.dispose();
                }
            });
            ships.forEach((s) => {
                s.mesh.traverse((obj) => {
                    if (obj.geometry) obj.geometry.dispose();
                    if (obj.material) obj.material.dispose();
                });
            });
            renderer.dispose();
        };
    }, []);

    return (
        <div
            ref={mountRef}
            className="absolute inset-0 w-full h-full"
            data-testid="hero-3d-globe"
            aria-hidden="true"
        />
    );
};

export default Hero3DGlobe;
